import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useFilter } from "../../../../hooks/useFilter";
import { useFilterContextV2 } from "../../../../contexts/FilterContextV2";
import Constants from "../../../../constants/Index";
import { Loading } from "../../../Loading/Index";
import { AlertModal } from "../../../Modal/Alert/Index";
import { TitleModal } from "../../../Modal/Contents/TitleModal/Index";
import { TextModal } from "../../../Modal/Contents/TextModal/Index";
import { ButtonRedirect } from "../../../Modal/Error/ButtonRedirect/Index";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { IPayloadProps } from "../../../../@types/Onboarding/IPayload";
import { OnboardingContext, OnboardingProvider, UserProgressProps } from "../../../../contexts/OnboardingContext";
import { useOnboarding } from "../../../../hooks/useOnboarding";
import axios from "../../../../services/axios";
import { ShapeFileV2 } from "../../../../pages/Onboarding/RegisterV2/ShapeFileV2";
import { CreateFarmsV2 } from "../../../../pages/Onboarding/RegisterV2/CreateFarmsV2/Index";
import { CreateAreasByFarmV2 } from "../../../../pages/Onboarding/RegisterV2/CreateAreasByFarmV2/Index";
import { CreateMicroAreasByFarmV2 } from "../../../../pages/Onboarding/RegisterV2/CreateMicroAreasByFarmV2/Index";
import { FarmMapCard } from "../../../../pages/Onboarding/RegisterV2/Summary/FarmMapCard";
import { OnboardingFooterV2 } from "../../../OnboardingV2/OnboardingFooterV2";
import { ButtonV2 } from "../../../FormControlsV2";
import { buildShapefileDraft, ShapefileUser } from "./draft";
import { ForemanStep, ForemanDraft, emptyForemanDraft } from "./ForemanStep";
import "../../../../pages/Onboarding/TemplateV2/styles.css";
import "../../../../pages/Onboarding/RegisterV2/Summary/styles.css";
import "./styles.css";

type ShapefileRegistrationProps = {
  stepsContainer?: HTMLElement | null;
};

export function ShapefileRegistration({ stepsContainer }: ShapefileRegistrationProps) {
  const { t } = useTranslation();
  const [initialPayload, setInitialPayload] = useState<IPayloadProps>();
  const [loadState, setLoadState] = useState<"loading" | "error" | "empty" | "ready">("loading");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoadState("loading");
    axios.get("/users/user").then(({ data }) => {
      if (!active) return;
      if (!Array.isArray(data)) throw new Error("Invalid users response");
      if (!data.length) { setLoadState("empty"); return; }
      setInitialPayload({
        users: data.map((user: any) => ({
          ...user,
          uuid: String(user.id ?? user.uuid),
        })),
        farms: [], areas: [], micro_areas: [], reason: [],
      } as unknown as IPayloadProps);
      setLoadState("ready");
    }).catch(() => { if (active) setLoadState("error"); });
    return () => { active = false; };
  }, [attempt]);

  if (loadState !== "ready" || !initialPayload) return (
    <section className="shapefile-import" aria-label={t("shapefileImport.title")}>
      <p role="status">{t(`shapefileImport.${loadState === "loading" ? "loadingUsers" : loadState === "empty" ? "noUsers" : "loadError"}`)}</p>
      {loadState !== "loading" && <ButtonV2 onClick={() => setAttempt(value => value + 1)}>{t("shapefileImport.retry")}</ButtonV2>}
    </section>
  );

  return (
    <OnboardingProvider persistent={false} initialPayload={initialPayload}>
      <ShapefileWizard stepsContainer={stepsContainer} />
    </OnboardingProvider>
  );
}

function ShapefileWizard({ stepsContainer }: ShapefileRegistrationProps) {
  const { t } = useTranslation();
  const context = useOnboarding();
  const { payload } = context;
  const [{ section, subSection }, setPosition] = useState({ section: 1, subSection: 0 });
  const [stepsOpen, setStepsOpen] = useState(false);
  const [foremanDrafts, setForemanDrafts] = useState<Record<string, ForemanDraft>>({});
  const stepsMenuRef = useRef<HTMLLIElement>(null);
  const stepsToggleRef = useRef<HTMLButtonElement>(null);
  const stepsMenuId = useId();

  useEffect(() => { setStepsOpen(false); }, [section, subSection]);

  useEffect(() => {
    if (!stepsOpen) return;
    const closeOutside = (event: MouseEvent) => {
      if (!stepsMenuRef.current?.contains(event.target as Node)) setStepsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setStepsOpen(false);
      stepsToggleRef.current?.focus();
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [stepsOpen]);

  const beforeStepBack = useRef<(() => boolean | Promise<boolean>) | null>(null);
  const jumping = useRef(false);
  const registerBeforeStepBack = useCallback((save: () => boolean | Promise<boolean>) => {
    beforeStepBack.current = save;
    return () => { if (beforeStepBack.current === save) beforeStepBack.current = null; };
  }, []);
  const farms = payload.farms || [];
  const summarySection = farms.length + 2;
  const isUpload = section === 1 && subSection === 0;
  const isSummary = section === summarySection;
  const farm = farms[section - 2];
  const labels = [
    { section: 1, names: [t("shapefileImport.title"), t("onboardingRemaining.farms")] },
    ...farms.map((farm, index) => ({
      section: index + 2,
      farm: farm.name,
      names: [t("shapefileForeman.title"), t("onboardingRemaining.area"), t("onboardingRemaining.paddock")],
    })),
    { section: summarySection, names: [t("onboardingRemaining.summary")] },
  ];
  const progress: UserProgressProps[] = labels.map(group => ({
    section: group.section,
    screens: group.names.map(name => ({ name, element: null, type: "nothing" })),
  }));

  function advance() {
    setPosition(current => current.subSection + 1 < (labels.find(group => group.section === current.section)?.names.length || 0)
      ? { ...current, subSection: current.subSection + 1 }
      : { section: current.section + 1, subSection: 0 });
  }

  function back() {
    setPosition(current => current.subSection > 0
      ? { ...current, subSection: current.subSection - 1 }
      : { section: Math.max(1, current.section - 1), subSection: (labels.find(group => group.section === current.section - 1)?.names.length || 1) - 1 });
  }

  async function returnToStep(targetSection: number, targetSubSection: number) {
    const earlier = targetSection < section || (targetSection === section && targetSubSection < subSection);
    if (!earlier || jumping.current) return;
    jumping.current = true;
    try {
      if (beforeStepBack.current && !(await beforeStepBack.current())) return;
      setPosition({ section: targetSection, subSection: targetSubSection });
      if (stepsOpen) stepsToggleRef.current?.focus();
    } finally {
      jumping.current = false;
    }
  }

  const stepCards = labels.map(group => {
    const state = group.section === section ? "current" : group.section < section ? "completed" : "upcoming";
    const title = "farm" in group ? group.farm : t(group.section === 1 ? "shapefileImport.importStep" : "shapefileImport.reviewStep");
    return (
      <li key={group.section} data-step-section={group.section} className={`shapefile-import__step shapefile-import__step--${state}`}>
        <div className="shapefile-import__step-heading">
          {"farm" in group && (
            <span className="shapefile-import__step-number" aria-hidden="true">
              {state === "completed" ? <FiCheck /> : String(group.section - 1).padStart(2, "0")}
            </span>
          )}
          <strong title={title}>{title}</strong>
        </div>
        <div className="shapefile-import__substeps">
          {group.names.map((name, index) => {
            const current = group.section === section && index === subSection;
            const completed = group.section < section || (group.section === section && index < subSection);
            const editableStep = group.section < summarySection;
            return (
              <button
                type="button"
                disabled={!editableStep || !completed}
                onClick={() => editableStep && returnToStep(group.section, index)}
                key={index}
                className={`shapefile-import__substep${completed ? " shapefile-import__substep--completed" : ""}`}
                aria-current={current ? "step" : undefined}
              >
                {completed ? <FiCheck aria-hidden="true" /> : <i aria-hidden="true" />}
                {name}
              </button>
            );
          })}
        </div>
      </li>
    );
  });

  const steps = (
    <ol className="shapefile-import__steps" aria-label={t("shapefileImport.steps")}>
      {stepCards[section - 1]}
      <li className="shapefile-import__steps-menu" ref={stepsMenuRef}>
        <button
          type="button"
          className="shapefile-import__steps-toggle"
          ref={stepsToggleRef}
          aria-expanded={stepsOpen}
          aria-controls={stepsMenuId}
          onClick={() => setStepsOpen(open => !open)}
        >
          {t("shapefileImport.otherSteps")}
          <span className="shapefile-import__steps-count">{labels.length - 1}</span>
          <FiChevronDown aria-hidden="true" />
        </button>
        <ol id={stepsMenuId} hidden={!stepsOpen} className="shapefile-import__steps-panel" aria-label={t("shapefileImport.otherSteps")}>
          {stepCards.filter((_, index) => index !== section - 1)}
        </ol>
      </li>
    </ol>
  );

  return (
    <OnboardingContext.Provider value={{
      ...context, screenNumber: section, subScreenNumber: subSection,
      userProgress: progress, advanceStep: advance, backStep: back, registerBeforeStepBack,
    }}>
      <section className="shapefile-import" aria-label={t("shapefileImport.title")}>
        {stepsContainer ? createPortal(steps, stepsContainer) : steps}
        <div className="shapefile-import__content">
          {/* Keep the selected file and its mapping when reviewing earlier steps. */}
          <div className="shapefile-import__upload" hidden={!isUpload}><ShapeFileV2 required onProcessed={() => {
            setForemanDrafts({});
            context.setPayload(current => ({ ...current, users: current.users.filter((user: ShapefileUser) => !user.isNewForShapefile) }));
          }} /></div>
          {section === 1 && subSection === 1 && <CreateFarmsV2 importFlow />}
          {!isSummary && farm && (subSection === 0
            ? <ForemanStep key={`${farm.uuid}-foreman`} farmId={farm.uuid}
                draft={foremanDrafts[farm.uuid] || emptyForemanDraft}
                onChange={draft => setForemanDrafts(current => ({ ...current, [farm.uuid]: draft }))} />
            : subSection === 1 ? <CreateAreasByFarmV2 key={`${farm.uuid}-areas`} farmId={farm.uuid} importFlow />
            : <CreateMicroAreasByFarmV2 key={`${farm.uuid}-pastures`} farmId={farm.uuid} importFlow />)}
          {isSummary && <ImportSummary />}
        </div>
      </section>
    </OnboardingContext.Provider>
  );
}

function ImportSummary() {
  const { t } = useTranslation();
  const { payload } = useOnboarding();
  const navigate = useNavigate();
  const { setSystemManager, setSideBarOptionSelected } = useFilter();
  const { reloadCatalogs } = useFilterContextV2();
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const requestStarted = useRef(false);
  const draft = buildShapefileDraft(payload);
  const submitting = status === "submitting";
  const completed = status === "success";

  async function handleConfirm() {
    if (requestStarted.current || completed) return false;
    requestStarted.current = true;
    setStatus("submitting");
    try {
      await axios.post("/shapefile", draft);
      setStatus("success");
    } catch {
      setStatus("error");
    } finally {
      requestStarted.current = false;
    }
    return false;
  }

  function handleModalOk() {
    if (!completed) {
      setStatus("idle");
      return;
    }
    reloadCatalogs();
    setSystemManager(null);
    setSideBarOptionSelected("MAPA");
    navigate(Constants.PADDOCKS_MAP);
  }

  return (
    <>
      <Loading visible={submitting} />
      <AlertModal visible={completed || status === "error"} contentStyle={{ width: "min(440px, calc(100vw - 32px))", height: "fit-content", maxHeight: "calc(100dvh - 32px)", overflowY: "auto", boxSizing: "border-box", borderRadius: 20, padding: 24, display: "flex", flexDirection: "column", justifyContent: "center", gap: 16 }}>
        <TitleModal>{t(completed ? "shapefileImport.successTitle" : "shapefileImport.errorTitle")}</TitleModal>
        <TextModal>{t(completed ? "shapefileImport.successMessage" : "shapefileImport.errorMessage")}</TextModal>
        {completed && draft.users.length > 0 && (
          <TextModal>{t("shapefileImport.defaultPassword", { password: Constants.DEFAULT_PASSWORD })}</TextModal>
        )}
        <ButtonRedirect onClick={handleModalOk}>{t("shapefileImport.ok")}</ButtonRedirect>
      </AlertModal>
      <main aria-busy={submitting}>
        <section className="shapefile-import__profiles">
          <h3 className="summary__title">{t("shapefileImport.profilesToCreate", { count: draft.users.length })}</h3>
          {draft.users.length > 0 ? (
            <div className="summary__section">
              <ul>
                {draft.users.map(user => (
                  <li key={user.uuid}>
                    <strong>{user.name}</strong>
                    <span> - {user.phone.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3")}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : <div className="summary__empty"><p>{t("shapefileImport.noNewProfiles")}</p></div>}
        </section>
        {draft.farms.map(farm => (
          <FarmMapCard key={farm.uuid} farm={farm} areas={draft.areas} microAreas={draft.micro_areas} />
        ))}
      </main>
      <OnboardingFooterV2
        nextStepEvent={handleConfirm}
        backStepEvent={() => !requestStarted.current && !completed}
        disabled={submitting || completed}
        nextLabel={t(submitting ? "shapefileImport.submitting" : "shapefileImport.confirm")}
      />
    </>
  );
}
